import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { ILWFCColumn } from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Workflow from '~/client/src/shared/models/Workflow'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { MaterialsDataKeys } from '../MaterialsDataKeys'
import BaseDataCellTable from './BaseDataCell/BaseDataCellTable'

// localization: translated

interface IProps {
  procurementId: string
  materialId: string
  forms: Workflow[]
  transferFormIconName: string
  onIdClick(form: Workflow): void

  footerTitle?: string
  shownColumnIds?: string[]
  isHeaderHidden?: boolean
  isFooterHidden?: boolean
  locationRenderer?(location: LocationBase): JSX.Element

  projectDateStore?: ProjectDateStore
  locationAttributesStore?: LocationAttributesStore
}

const ROW_HEIGHT = 40

@inject('projectDateStore', 'locationAttributesStore')
@observer
export default class MaterialTransferCellTable extends React.Component<IProps> {
  @observable private localProps: IProps

  public constructor(props: IProps) {
    super(props)
    this.localProps = props

    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const { isHeaderHidden, isFooterHidden, transferFormIconName } = this.props
    return (
      <BaseDataCellTable<Workflow>
        items={this.props.forms}
        columns={this.columns}
        getRowHeightByItem={this.getRowHeightByForm}
        getCellContentByDataKey={this.getCellContentByDataKey}
        getFooterTitleByDataKey={this.getFooterTitleByDataKey}
        onIdClick={this.onIdClick}
        iconElement={
          <WorkflowTypeIcon
            iconName={transferFormIconName}
            className="form-id-icon-large relative no-flex mr5"
          />
        }
        isHeaderHidden={isHeaderHidden}
        isFooterHidden={isFooterHidden}
      />
    )
  }

  private getCellContentByDataKey = (
    dataKey: string,
    form: Workflow,
  ): JSX.Element => {
    const {
      procurementId,
      materialId,
      projectDateStore: { getMonthDayYearAndTimeToDisplay },
    } = this.props
    switch (dataKey) {
      case MaterialsDataKeys.ID:
        return <>{form.code}</>
      case MaterialsDataKeys.MATERIAL_TRANSFER_DATE:
        return (
          <span className="text-ellipsis">
            {getMonthDayYearAndTimeToDisplay(form.startDate)}
          </span>
        )
      case MaterialsDataKeys.PLANNED_QUANTITY:
        return (
          <span className="text large end ellipsis">
            {(procurementId
              ? form.procurementQuantitiesMap[procurementId]
              : form.materialQuantitiesMap[materialId]) || 0}
          </span>
        )
      case MaterialsDataKeys.STATUS:
        return (
          <StatusBadge
            className="no-grow"
            status={form.status}
            workflowStepLevel={form.workflowStepLevel}
            isLate={form.isLate}
          />
        )
      case MaterialsDataKeys.CURRENT_MATERIAL_LOCATION:
        return this.renderLocations(
          form.getMaterialLocationIds(materialId, procurementId),
        )
      case MaterialsDataKeys.PLANNED_INSTALL_LOCATION:
        return this.renderLocations([form.locations[0]?.id])
      default:
        return null
    }
  }

  private renderLocations = (locationIds: string[]): JSX.Element => {
    const {
      locationAttributesStore: { getById },
      locationRenderer,
    } = this.props

    if (!locationIds?.length) {
      return locationRenderer(null)
    }

    return (
      <div className="col overflow-hidden">
        {locationIds.map(id => (
          <div key={id} className="row py2">
            {locationRenderer(getById(id))}
          </div>
        ))}
      </div>
    )
  }

  private getFooterTitleByDataKey = (dataKey: string) => {
    const { footerTitle, forms, materialId, procurementId } = this.localProps

    switch (dataKey) {
      case MaterialsDataKeys.ID:
        return footerTitle
      case MaterialsDataKeys.PLANNED_QUANTITY:
        return forms.reduce(
          (sum, form) =>
            (sum +=
              (procurementId
                ? form.procurementQuantitiesMap[procurementId]
                : form.materialQuantitiesMap[materialId]) || 0),
          0,
        )
      default:
        return null
    }
  }

  private get columns(): ILWFCColumn[] {
    return [
      {
        label: Localization.translator.id_short,
        dataKey: MaterialsDataKeys.ID,
        width: 140,
      },
      {
        label: Localization.translator.bookingDate,
        dataKey: MaterialsDataKeys.MATERIAL_TRANSFER_DATE,
        width: 180,
      },
      {
        label: Localization.translator.quantity,
        dataKey: MaterialsDataKeys.PLANNED_QUANTITY,
        width: 80,
      },
      {
        label: Localization.translator.status,
        dataKey: MaterialsDataKeys.STATUS,
        width: 125,
      },
      {
        label: Localization.translator.locationFrom,
        dataKey: MaterialsDataKeys.CURRENT_MATERIAL_LOCATION,
        width: 160,
      },
      {
        label: Localization.translator.locationTo,
        dataKey: MaterialsDataKeys.PLANNED_INSTALL_LOCATION,
        width: 160,
      },
    ].filter(
      ({ dataKey }) =>
        dataKey === MaterialsDataKeys.ID ||
        this.props.shownColumnIds.includes(dataKey),
    )
  }

  private getRowHeightByForm = (form: Workflow): number => {
    if (!form) {
      return 0
    }

    const { procurementId, materialId, shownColumnIds } = this.localProps
    if (!shownColumnIds.includes(MaterialsDataKeys.CURRENT_MATERIAL_LOCATION)) {
      return ROW_HEIGHT
    }

    const rowNumbers = form.getMaterialLocationIds(
      materialId,
      procurementId,
    ).length

    return (rowNumbers || 1) * ROW_HEIGHT
  }

  private onIdClick = (form: Workflow) => {
    this.props.onIdClick(form)
  }
}
